/**
 * Weapon icon family — World Art Pack 2, Task 4.
 *
 * One procedural generator per weapon `IconKind` (10 kinds, covering every
 * `type === 'weapon'` item in ITEM_DATABASE). Each painter draws flat,
 * silhouette-first inner art in the central 24x24 space per
 * `docs/design/coil-art/ICON_DESIGN_SHEETS.md` (2-3 value steps, top-left
 * light, 45-degree hero diagonal — shield is the one front-facing exception)
 * and hands it to `framedIcon`, which owns ALL framing (translate(4,4),
 * rarity ring, top-tier halo) and returns the base64 data URL the registry
 * ladder expects.
 *
 * `look.tint` (elemental keyword inference from iconLook.ts) colors the
 * staff orb and the wand star; every other kind ignores it — steel and wood
 * read better untinted at 20px, and the ring already carries rarity color.
 *
 * Registration happens in ItemIconRegistry.ts (the single composition seam);
 * this file only imports a TYPE from the registry, so there is no runtime
 * import cycle.
 */
import { framedIcon } from './rarityFrame';
import { inferIconLook, type IconKind, type IconLook } from './iconLook';
import type { ProceduralIconGenerator } from '../ItemIconRegistry';
import type { ItemDefinition } from '@/shared/content/generated/items.gen';

// Shared material palette — 2-3 value steps per material, top-left light.
const STEEL = '#c9ccd4';
const STEEL_LIGHT = '#eef1f6';
const STEEL_MID = '#9ba1ad';
const STEEL_DARK = '#8a8f9a';
const WOOD = '#7a5a2e';
const WOOD_DARK = '#5a4020';
const WOOD_LIGHT = '#a07a3e';
const BOWSTRING = '#e8ebf2';
const FLETCHING = '#e8e4da';
/** Default staff-orb arcane violet when the item carries no elemental tint. */
const STAFF_ORB_DEFAULT = '#b98bff';
/** Default wand-star gold when the item carries no elemental tint. */
const WAND_STAR_DEFAULT = '#ffd76b';

type KindPainter = (look: IconLook, size: number) => string;

/** Rotate vertically-authored art (head at top, grip at bottom) onto the
 *  45-degree hero diagonal (head to the top-right, grip bottom-left). */
export const diag = (inner: string) => `<g transform="rotate(45 12 12)">${inner}</g>`;

// --- sword — the brief's worked template, verbatim ------------------------
const sword: KindPainter = (look, size) => framedIcon(`
    <polygon points="20,5 23,8 11,20 8,17" fill="#c9ccd4"/>
    <polygon points="20,5 23,8 22,9 19,6" fill="#eef1f6"/>
    <rect x="7.2" y="16.2" width="7" height="2.4" rx="1" transform="rotate(-45 10.7 17.4)" fill="#7a5a2e"/>
    <rect x="6" y="19" width="3.4" height="3.4" rx="1.2" transform="rotate(-45 7.7 20.7)" fill="#5a4020"/>
  `, look.rarity, size);

// --- axe — bearded single-blade + haft -------------------------------------
const axe: KindPainter = (look, size) => framedIcon(diag(`
    <rect x="11.15" y="5" width="1.7" height="16" rx="0.85" fill="${WOOD}"/>
    <rect x="10.8" y="18.6" width="2.4" height="2.6" rx="1" fill="${WOOD_DARK}"/>
    <path d="M12.6 4.4 Q7 3.8 4.8 5.6 Q4 9.4 6.2 12 L8.4 13.6 Q9.2 9.8 12.6 9.2 Z" fill="${STEEL}"/>
    <path d="M4.8 5.6 Q4 9.4 6.2 12 L7.4 12.9 Q5.3 9.7 6 6 Z" fill="${STEEL_LIGHT}"/>
  `), look.rarity, size);

// --- mace — flanged head (broad 6-point flange star behind a steel core;
// spikes kept fat + light so they still read at 20px) -----------------------
const mace: KindPainter = (look, size) => framedIcon(diag(`
    <rect x="11.15" y="10" width="1.7" height="11" rx="0.85" fill="${WOOD}"/>
    <rect x="10.8" y="18.8" width="2.4" height="2.4" rx="1" fill="${WOOD_DARK}"/>
    <polygon points="12,0.8 13.5,3.4 16.5,3.4 15,6 16.5,8.6 13.5,8.6 12,11.2 10.5,8.6 7.5,8.6 9,6 7.5,3.4 10.5,3.4" fill="${STEEL}"/>
    <circle cx="12" cy="6" r="2.7" fill="${STEEL_MID}"/>
    <circle cx="11" cy="5" r="0.9" fill="${STEEL_LIGHT}"/>
  `), look.rarity, size);

// --- dagger — short blade + wide guard --------------------------------------
const dagger: KindPainter = (look, size) => framedIcon(diag(`
    <polygon points="12,3.5 14.2,12.8 9.8,12.8" fill="${STEEL}"/>
    <polygon points="12,3.5 12,12.8 9.8,12.8" fill="${STEEL_LIGHT}"/>
    <rect x="7.6" y="12.6" width="8.8" height="2" rx="1" fill="${STEEL_DARK}"/>
    <rect x="10.9" y="14.8" width="2.2" height="4.6" rx="1" fill="${WOOD_DARK}"/>
    <circle cx="12" cy="20.2" r="1.3" fill="${WOOD}"/>
  `), look.rarity, size);

// --- bow — arc + string + nocked arrow (arrow flies to the top-right) ------
const bow: KindPainter = (look, size) => framedIcon(diag(`
    <path d="M4.5 13.2 Q12 3 19.5 13.2" fill="none" stroke="${WOOD}" stroke-width="1.8" stroke-linecap="round"/>
    <line x1="4.5" y1="13.2" x2="19.5" y2="13.2" stroke="${BOWSTRING}" stroke-width="0.8"/>
    <rect x="11.4" y="5.2" width="1.2" height="12.4" fill="${WOOD_LIGHT}"/>
    <polygon points="12,2.4 10.2,6.2 13.8,6.2" fill="${STEEL}"/>
    <polygon points="12,2.4 12,6.2 10.2,6.2" fill="${STEEL_LIGHT}"/>
    <polygon points="12,15.4 9.9,18.6 12,17.7" fill="${FLETCHING}"/>
    <polygon points="12,15.4 14.1,18.6 12,17.7" fill="${FLETCHING}"/>
  `), look.rarity, size);

// --- staff — full-diagonal shaft + orb tip (orb takes look.tint) ------------
const staff: KindPainter = (look, size) => {
  const orb = look.tint ?? STAFF_ORB_DEFAULT;
  return framedIcon(diag(`
    <rect x="11.15" y="8.2" width="1.7" height="13.6" rx="0.85" fill="${WOOD}"/>
    <rect x="10.5" y="8.4" width="3" height="1.7" rx="0.8" fill="${WOOD_DARK}"/>
    <circle cx="12" cy="5.4" r="3.9" fill="${orb}" opacity="0.28"/>
    <circle cx="12" cy="5.4" r="3" fill="${orb}"/>
    <circle cx="10.9" cy="4.3" r="1" fill="#ffffff" opacity="0.75"/>
  `), look.rarity, size);
};

// --- wand — short shaft + star tip (star takes look.tint) -------------------
const wand: KindPainter = (look, size) => {
  const star = look.tint ?? WAND_STAR_DEFAULT;
  return framedIcon(diag(`
    <rect x="11.2" y="9.8" width="1.6" height="9.4" rx="0.8" fill="${WOOD}"/>
    <rect x="10.9" y="17.6" width="2.2" height="1.8" rx="0.9" fill="${WOOD_DARK}"/>
    <polygon points="12,2.4 13.1,5.7 16.4,6.8 13.1,7.9 12,11.2 10.9,7.9 7.6,6.8 10.9,5.7" fill="${star}"/>
    <circle cx="12" cy="6.8" r="1" fill="#ffffff" opacity="0.9"/>
    <circle cx="16.8" cy="3.2" r="0.7" fill="${star}" opacity="0.8"/>
    <circle cx="7.9" cy="10.9" r="0.55" fill="${star}" opacity="0.7"/>
  `), look.rarity, size);
};

// --- shield — front-facing heater + boss (the one non-diagonal kind) --------
const shield: KindPainter = (look, size) => framedIcon(`
    <path d="M12 2.6 C14.5 4 17.5 4.6 20.4 4.6 C20.4 12.4 17.4 18 12 21.6 C6.6 18 3.6 12.4 3.6 4.6 C6.5 4.6 9.5 4 12 2.6 Z" fill="${STEEL_DARK}"/>
    <path d="M12 2.6 C9.5 4 6.5 4.6 3.6 4.6 C3.6 12.4 6.6 18 12 21.6 Z" fill="${STEEL_MID}"/>
    <path d="M12 2.6 C14.5 4 17.5 4.6 20.4 4.6 C20.4 12.4 17.4 18 12 21.6 C6.6 18 3.6 12.4 3.6 4.6 C6.5 4.6 9.5 4 12 2.6 Z" fill="none" stroke="#5b606b" stroke-width="1.2"/>
    <circle cx="12" cy="10.5" r="2.7" fill="${STEEL}"/>
    <circle cx="11.1" cy="9.6" r="0.9" fill="${STEEL_LIGHT}"/>
  `, look.rarity, size);

// --- fist — knuckle claws over a leather wrap --------------------------------
const fist: KindPainter = (look, size) => framedIcon(diag(`
    <path d="M9.4 13.6 Q7.4 9 8.4 4.6 Q10.3 9 10.9 13.5 Z" fill="${STEEL}"/>
    <path d="M11.2 13.4 Q10.9 7.8 12.1 3.2 Q13.3 7.8 12.9 13.4 Z" fill="${STEEL}"/>
    <path d="M13.2 13.5 Q13.8 9 15.7 4.6 Q16.7 9 14.7 13.6 Z" fill="${STEEL}"/>
    <path d="M12.1 3.2 Q10.9 7.8 11.2 13.4 L11.9 13.4 Q11.6 7.8 12.1 3.2 Z" fill="${STEEL_LIGHT}"/>
    <rect x="8.1" y="13.2" width="7.8" height="6.4" rx="2.7" fill="${WOOD}"/>
    <rect x="8.1" y="15.7" width="7.8" height="1.7" fill="${WOOD_DARK}"/>
    <circle cx="10" cy="14.7" r="0.7" fill="${WOOD_LIGHT}"/>
    <circle cx="12.1" cy="14.5" r="0.7" fill="${WOOD_LIGHT}"/>
    <circle cx="14.2" cy="14.7" r="0.7" fill="${WOOD_LIGHT}"/>
  `), look.rarity, size);

// --- polearm — long haft + leaf blade ----------------------------------------
const polearm: KindPainter = (look, size) => framedIcon(diag(`
    <rect x="11.2" y="8.6" width="1.6" height="13.2" rx="0.8" fill="${WOOD}"/>
    <rect x="10.7" y="8.6" width="2.6" height="1.7" rx="0.8" fill="${WOOD_DARK}"/>
    <path d="M12 1.8 Q15 5.2 12 9.6 Q9 5.2 12 1.8 Z" fill="${STEEL}"/>
    <path d="M12 1.8 Q9 5.2 12 9.6 Z" fill="${STEEL_LIGHT}"/>
  `), look.rarity, size);

/** The 10 weapon kinds this family owns (mirrors the IconKind weapon row). */
export const WEAPON_ICON_KINDS: readonly IconKind[] = [
  'sword', 'axe', 'mace', 'dagger', 'bow', 'staff', 'wand', 'shield', 'fist', 'polearm',
];

const PAINTERS: Readonly<Partial<Record<IconKind, KindPainter>>> = {
  sword, axe, mace, dagger, bow, staff, wand, shield, fist, polearm,
};

/** Adapt a look-based painter to the registry's (item, size) contract. */
const asGenerator = (paint: KindPainter): ProceduralIconGenerator =>
  (item: ItemDefinition, size: number) => paint(inferIconLook(item), size);

export const WEAPON_KIND_ICONS: Partial<Record<IconKind, ProceduralIconGenerator>> =
  Object.freeze(Object.fromEntries(
    WEAPON_ICON_KINDS.map((kind) => [kind, asGenerator(PAINTERS[kind]!)]),
  ));
